import { trpc } from '@tencent/tpro_rmonitor_login_server_login_auth';
import { observable, makeObservable } from 'mobx';
import { getUserId, getStorageAuthInfo, isInternal } from '@/infrastructure/auth';
import { getAxios, TRPCGet, TRPCPost } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import {
  getParamFromUrl,
  getHistory,
  pushHistory,
  toSearchObj,
} from '@/infrastructure/history';
import { logger } from '@/infrastructure/logger';
import { reporter } from '@/infrastructure/reporter/datong';
import { storageGet, storageSet } from '@/infrastructure/storage-utils';
import { Platform } from '@/infrastructure/utils/app';
import { getCachedFilters, setCachedFilters } from '@/presentation/components/form/utils';
import { Toggle } from '@/presentation/components/toggle/toggle';
import { PRODUCTS_SORT_STORAGE } from '@/presentation/config/storage';
import { getAllAppIDs } from '@/presentation/pages/product/info/api';
import { getObjectLabel, IConfigObject, IFilterConfig } from '@/presentation/pages/setting/data-labeling/config';
import { SystemID } from '@/proto/appid';
import { filterRuleService } from '@/service/filter-rule';
import { productOAServer } from '@/service/productoa';
import { ProductInfo, userService } from '@/service/user';
import { userRoleService } from '@/service/user-role';
import { WithTypeCast } from '@raft/utils';
type IUserInfo = WithTypeCast<trpc.rmonitor.login_server.IUserInfo>;

export interface Product {
  productId: string;        // 产品app_id
  appKey: string;           // 产品app_key
  productName: string;      // 产品名称
  owner: string;            // 负责人
  pid: string;              // 平台类型
  logoUrl: string;
  isFlutter: number;
  hasPermission: boolean;
}

type RawUserInfo = {
  isSuperUser: boolean;
  isAuth: boolean;
  roleId: string | number;
  roleName: string;
  rolePermissions: string[];
  expireTime: number;
};

type RawPerm = {
  isAdmin: boolean,
  permissions: string[],
  roleId: string | number;
  expireTime: number;
};

export class ProductStore {
  loaded = false;
  products: ProductInfo[] = [];
  product: ProductInfo | undefined = undefined;
  productId = '';                           // 当前选中的平台
  pid = '';                                 // 兼容bugly的pid
  permission = true;                        // 当前选中的产品组的权限
  isFlutter = false;
  isSuperUser = false;
  rawPerm: RawPerm = {
    isAdmin: false,
    permissions: [],
    roleId: '',
    expireTime: 0,
  };
  // 过滤黑产
  filterConfig: IFilterConfig | undefined;

  // 外网版本用
  hasProduct = true;

  // Tooly appid
  toolyAppId = '';

  public constructor() {
    makeObservable(this, {
      loaded: observable,
      products: observable,
      product: observable,
      productId: observable,
      pid: observable,
      permission: observable,
      isFlutter: observable,
      isSuperUser: observable,
      rawPerm: observable,
      filterConfig: observable,
    });

    if (!isInternal()) {
      this.init();
    } else {
      const user = getStorageAuthInfo();
      this.hasProduct = user?.userInfo?.hasProduct ?? true;
    }
  }

  public setHasProduct = async (hasProduct: boolean) => {
    this.hasProduct = hasProduct;
  };

  public setPermission = async (permission: boolean) => {
    this.permission = permission;
  };

  /**
   * 加载产品组列表
   */
  public loadProducts = async () => {
    logger.info('ProductStore|loadGroupProducts');
    if (isInternal()) {
      this.products = await this.getAppList();
    } else {
      this.products = await this.getAppListForOA();
    }
  };

  /**
   * 设置产品 id
   * @param productId
   * @param updateUrl
   */
  public setId = async (productId: string, updateUrl = false) => {
    logger.info('ProductStore|setId, productId:', productId, this.productId);
    if (productId === this.productId) {
      return;
    }

    this.productId = productId;
    // 缓存产品排序
    this.handlelocalProductsSort(productId);
    this.product = this.products.find(product => product.productId === productId);

    if (!this.product) {
      this.product = await this.getAppInfoById(productId);
    }

    if (this.isSuperUser) {
      this.permission = true;
    } else {
      this.permission = await this.checkPermission(productId) || false;
    }

    // 更新角色权限
    const rawPerm = await this.getPermissions(productId, getUserId());
    this.rawPerm = rawPerm;
    await this.setFilterConfig(productId, this.pid);

    // 设置上报的产品信息
    reporter?.setPublicParams?.({
      productId,
      productName: this.product?.productName,
    });

    const oaName = getUserId();
    this.pid = this.product?.platformId || '0';
    this.isFlutter = Boolean(this.product?.isFlutter);
    storageSet(`${oaName}-productId`, this.productId);
    storageSet(`${oaName}-pid`, this.pid);
    await this.updateToolyAppId();
    if (updateUrl) {
      const history = getHistory();
      pushHistory(history, history.location.pathname, {
        ...toSearchObj(history.location.search),
        productId,
        pid: this.pid,
      });
    }
  };

  /**
   * 判断是不是成员权限
   * @param productId
   */
  public checkPermission = async (productId: string) => {
    if (isInternal()) {
      return await userService.canAccessProduct({ productId });
    }

    return await this.checkUserInTenant(productId);
  };

  public checkUserInTenant = async (productId: string) => {
    try {
      const resp = await TRPCGet(`/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/CheckUserInTenant?productId=${productId}`);
      return resp?.isAuth ?? false;
    } catch (e) {
      logger.error('checkUserInTenant err', e);
      return false;
    }
  };

  public isSuperUserForOA = async () => {
    try {
      const resp = await TRPCGet(`/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/IsSuperManager`);
      return resp?.isSuperManager ?? false;
    } catch (e) {
      logger.error('isSuperUserForOA err', e);
      return false;
    }
  };

  public getPermissions = async (productId: string, userId: string) => {
    try {
      const resp = await TRPCPost(
        `/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/GetRoleByUser`,
        {
          productId,
          username: userId,
        },
        {
          ignoreError: true,
        },
      );
      const roleId = resp?.role?.id;
      const permissions = await userRoleService.getRolePermission({
        roleId,
      });
      return {
        permissions,
        roleId,
        isAdmin: resp?.role?.name === '管理员',
        expireTime: resp?.role?.expireTime,
      };
    } catch (e) {
      return {
        permissions: [],
        roleId: '',
        isAdmin: false,
        expireTime: 0,
      };
    }
  };

  public getUserInfo = async (productId: string) => await TRPCGet(`/${API_PREFIX}/api/user/info?productId=${productId}`);

  /**
   * 初始化设置
   */
  public init = async () => {
    logger.info('ProductStore|init');
    this.loaded = false;
    let productId = getParamFromUrl('productId');

    if (!productId) {
      productId = storageGet(`${getUserId()}-productId`) || '';
    }

    // 先查权限
    const rawUserInfo: RawUserInfo = productId ? await this.getUserInfo(productId) : undefined;

    // 是否有权限 超管or产品成员
    const hasPermission = rawUserInfo?.isSuperUser || rawUserInfo?.isAuth || false;
    if (rawUserInfo && hasPermission) {
      // 有权限才去拉产品数据
      const info = productId ? await this.getAppInfoById(productId) : undefined;
      this.product = info;
      this.productId = info?.productId || productId;
      this.handlelocalProductsSort(info?.productId || productId);
      this.pid = info?.platformId || '0';
      this.isFlutter = Boolean(info?.isFlutter);
      if (rawUserInfo) {
        this.isSuperUser = rawUserInfo.isSuperUser;
        this.permission = rawUserInfo.isSuperUser || rawUserInfo.isAuth || false;
        this.rawPerm = {
          isAdmin: rawUserInfo.roleName === '管理员',
          roleId: rawUserInfo.roleId,
          permissions: rawUserInfo.rolePermissions,
          expireTime: rawUserInfo.expireTime,
        };
      }
      await this.updateToolyAppId();
      await this.setFilterConfig(productId, this.pid);
    } else {
      this.product = undefined;
      this.productId = productId;
      this.pid = '0';
      this.isFlutter = false;
      this.isSuperUser = false;
      this.permission = false;
      this.rawPerm = {
        isAdmin: false,
        roleId: 0,
        permissions: [],
        expireTime: 0,
      };
    }

    // 设置上报的产品信息
    reporter?.setPublicParams?.({
      productId,
      productName: this.product?.productName,
    });

    this.loaded = true;
  };

  public setFilterConfig = async (productId: string, pid?: string) => {
    if (![Platform.Android, Platform.IOS].includes(Number(pid)) || this.isFlutter) {
      return;
    }
    const config =  await filterRuleService.getConfig({
      productId,
      platformId: Number(pid),
    });
    const filterRules = await filterRuleService.getFilterRules({
      productId,
    });
    const configObject: Record<string, IConfigObject> = {};
    config.monitorItems?.forEach((item) => {
      if (item.monitorKey) {
        configObject[item.monitorKey] = {
          values: filterRules?.filter(rulesItem => rulesItem.monitorKey === item.monitorKey)
            ?.map(item => ({ value: item.labelKey || '', label: item.labelName || '' })) || [],
          labelOptions: getObjectLabel(item.monitorKey, config.labels) || [],
        };
      }
    });
    this.filterConfig = {
      config,
      configObject,
      filterRules,
    };
  };

  public initForInternal = async () => {
    logger.info('ProductStore|initForInternal');
    this.loaded = false;

    const initUserTasks: [
      Promise<IUserInfo | null>,
      Promise<boolean>,
    ] = [
      userService.getUserInfo(),
      userService.checkSuperAdmin({}),
    ];
    const [userInfo, isSuperUser] = await Promise.all(initUserTasks);
    this.isSuperUser = isSuperUser;
    const hasProduct = userInfo?.hasProduct ?? false;
    this.hasProduct = isSuperUser ? true : hasProduct;

    const productId = getParamFromUrl('productId') || storageGet(`${getUserId()}-productId`);

    if (productId) {
      const getAppInfoTasks: [
        Promise<ProductInfo | undefined>,
        Promise<boolean>,
        Promise<RawPerm>
      ] = [
        this.getAppInfoById(productId),
        this.checkPermission(productId),
        this.getPermissions(productId, getUserId()),
      ];
      const [info, canAccess, rawPerm] = await Promise.all(getAppInfoTasks);
      this.product = info;
      this.productId = info?.productId || productId;
      // 缓存产品排序
      this.handlelocalProductsSort(info?.productId || productId);
      this.pid = info?.platformId || '0';
      this.isFlutter = !!info?.isFlutter;
      this.permission = isSuperUser || canAccess || false;
      this.rawPerm = rawPerm;

      await this.setFilterConfig(productId, this.pid);
      // 设置上报的产品信息
      reporter?.setPublicParams?.({
        productId,
        productName: this.product?.productName,
      });
    }

    this.loaded = true;
  };

  // 获取Tooly AppId
  private updateToolyAppId = async () => {
    if (isInternal()) {
      return;
    }
    const res =  await getAllAppIDs({
      systemId: SystemID.RCONFIG,
      platformId: this.pid ? Number(this.pid) : undefined,
      bundleId: this.product?.bundleId,
    });
    this.toolyAppId = res?.AppIDInfo?.rconfigAppID || '';
  };

  private getAppList = async (): Promise<ProductInfo[]> => {
    try {
      const res = await userService.getProductList();
      return res.map(product => ({
        ...product,
        hasPermission: true,
      }));
    } catch (e) {
      logger.error('getAppList:', e);
      return [];
    }
  };

  private getAppListForOA = async (): Promise<ProductInfo[]> => {
    // TODO 接口切换暂时添加白名单
    const productId = getParamFromUrl('productId')
      || storageGet(`${getUserId()}-productId`)
      || this.productId || '';
    const toggle = new Toggle();
    const enable = await toggle.isEnable('product', ['user'], {
      user: getUserId(),
      appId: productId,
    });

    try {
      if (enable) {
        const res = await productOAServer.getProductOaList({ userId: getUserId() });
        return res?.map<ProductInfo>(item => ({
          productId: item?.appId || '',
          appKey: item?.appKey || '',
          productName: item?.name || '',
          owner: item?.ownerId || '',
          logoUrl: '',
          pid: `${item?.platformId || 1}`,
          platformId: `${item?.platformId || 1}`,
          isFlutter: Boolean(item?.isFlutter || 0),
          hasPermission: true,
          bundleId: item?.bundleId,
          expireTime: '',
        })) || [];
      }
      const res = await getAxios().get(`/${API_PREFIX}/api/init`);
      return res?.data?.appList?.map((item: {
        appId: string;
        appKey: string;
        appName: string;
        ownerId: string;
        logoUrl: string;
        pid: string | number;
        isFlutter: number;
        related: {bundleId: string}
      }) => ({
        productId: item?.appId || '',
        appKey: item?.appKey || '',
        productName: item?.appName || '',
        owner: item?.ownerId || '',
        logoUrl: item?.logoUrl || '',
        pid: `${item?.pid || 1}`,
        platformId: `${item?.pid || 1}`,
        isFlutter: item?.isFlutter || 0,
        hasPermission: true,
        bundleId: item?.related?.bundleId,
      })) || [];
    } catch (e) {
      logger.error('getProductOaList:', e);
      return [];
    }
  };

  private getAppInfoById = async (productId: string): Promise<ProductInfo | undefined> => {
    // TODO 接口切换暂时添加白名单
    const toggle = new Toggle();
    const enable = await toggle.isEnable('product', ['user'], {
      user: getUserId(),
      appId: productId,
    });

    try {
      if (enable) {
        const info = await productOAServer.getProductOaInfo({ appId: productId });
        return {
          productId: info?.appId || productId,
          appKey: info?.appKey || '',
          productName: info?.name || '',
          owner: info?.ownerId,
          logoUrl: '',
          platformId: `${info?.platformId || 0}`,
          isFlutter: Boolean(info?.isFlutter),
          hasPermission: info?.hasPermission || false,
          bundleId: info?.bundleId,
          diagnoseGuidAlias: info?.diagnoseGuidAlias,
          expireTime: '',
        };
      }
      const res = await getAxios().get(`/${API_PREFIX}/api/app/info`, {
        params: {
          appId: productId,
        },
      });
      const info = res?.data?.data;

      return {
        productId: info?.appId || productId,
        appKey: info?.appKey,
        productName: info?.name,
        owner: info?.ownerId,
        logoUrl: info?.logoUrl,
        platformId: `${info?.platformId || 0}`,
        isFlutter: info?.isFlutter,
        hasPermission: info?.hasPermission || false,
        bundleId: info?.related?.bundleId,
        diagnoseGuidAlias: info?.diagnoseGuidAlias,
        expireTime: info?.expireTime,
      };
    } catch (e) {
      logger.error('getAppInfoById:', e);
      return undefined;
    }
  };

  private handlelocalProductsSort = (productId: string) => {
    const storageProducts = getCachedFilters(PRODUCTS_SORT_STORAGE) || [];
    if (!Array.isArray(storageProducts)) {
      setCachedFilters(PRODUCTS_SORT_STORAGE, [productId]);
      return;
    }
    const newStorageProducts = storageProducts.filter(id => id !== productId);
    newStorageProducts.unshift(productId);
    setCachedFilters(PRODUCTS_SORT_STORAGE, newStorageProducts);
  };
}
